import { Component, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, Subject, forkJoin, Observable } from 'rxjs';
import { takeUntil, switchMap, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { PlanDialogResult, PlanEntry } from './add-plan-dialog/add-plan-dialog.component';
import { TimeEntryForPlanningService, BatchPlanEntryPayload, BatchSaveResponse } from './time-entry-for-planning.service';
import { PlanEntryService } from '../../common/services/plan-entry.service';
import { AuthenticationService } from '../../../../login/login-page/authentication.service';
import { SnackbarService } from '../../common/services/snackbar.service';
import { Constants } from '../../common/utilities/constants.config';
import { Store } from '@ngrx/store';
import { getLoggedInUser } from 'app/core/store/loggedInUser/loggedInUser.selectors';

interface LoadEntriesRequest {
  dateFrom: string;
  dateTo: string;
  client?: string;
  project?: string;
  consultant?: string;
  charge?: string;
  onSuccess?: () => void;
}

@Component({
  selector: 'app-time-entry-for-planning',
  templateUrl: './time-entry-for-planning.component.html',
  styleUrls: ['./time-entry-for-planning.component.scss']
})
export class TimeEntryForPlanningComponent implements OnInit, OnDestroy {

  // Current data shown in the grid
  planningEntries: PlanEntry[] = [];
  visibleEntries: PlanEntry[] = [];

  // Last successfully saved state
  private savedPlanningEntries: PlanEntry[] = [];
  private serverEntryIds = new Set<number>();

  selectedEntryIds: number[] = [];

  // Save is disabled until something is changed
  isSaveDisabled = true;
  isSaving = false; 
  showClashCount = false;

  showEntryPanel = false;
  filterFullyCollapsed = false;

  entryPanelData: PlanEntry | null = null;
  loggedInUsername: string = '';
  planVersionId: number | null = null;
  private isFetchingPlanVersion = false;

  private activeFilters = {
    clients: [] as string[],
    projects: [] as string[],
    employees: [] as string[],
    dateStart: null as string | null,
    dateEnd: null as string | null,
    chargedFilter: 'All' as 'All' | 'Charged' | 'Uncharged',
  };

  private destroy$ = new Subject<void>();

  private loadEntriesParams$ = new Subject<LoadEntriesRequest>();

  constructor(
    private planningService: TimeEntryForPlanningService,
    private planEntryService: PlanEntryService,
    private authenticationService: AuthenticationService,
    private _snackBarService: SnackbarService,
    private _store: Store
  ) {}

  totalHours = 0;

  get rowCount(): number {
    return this.visibleEntries.filter(
      entry => !entry.isPlaceholder
    ).length;
  }

  // Counts distinct dates with a clash, not entries — two clashing entries
  // on the same date should only count as one clash for the summary chip
  get clashCount(): number {
    const clashedDates = new Set(
      this.visibleEntries
        .filter(entry => !entry.isPlaceholder && entry.causedClash)
        .map(entry => entry.date)
    );
    return clashedDates.size;
  }

  viewMode: 'grid' | 'calendar' = 'grid';
  errorMessage: string = '';

  get periodStart(): string | null {
    return this.activeFilters.dateStart;
  }

  get periodEnd(): string | null {
    return this.activeFilters.dateEnd;
  }

  // ----- Init / destroy -----

  ngOnInit(): void {
    combineLatest([
      this.planningService.clientFilter$,
      this.planningService.projectFilter$,
      this.planningService.employeeFilter$,
      this.planningService.dateRange$,
      this.planningService.chargedFilter$,
      this.planningService.showClashes$,
    ])
      .pipe(
        debounceTime(0),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        takeUntil(this.destroy$)
      )
      .subscribe(
        ([clients, projects, employees, dateRange, chargedFilter]) => {
          this.activeFilters = {
            clients: clients || [],
            projects: projects || [],
            employees: employees || [],
            dateStart: dateRange?.start ?? null,
            dateEnd: dateRange?.end ?? null,
            chargedFilter: chargedFilter || 'All',
          };

          const dateFrom = dateRange?.start ?? null;
          const dateTo = dateRange?.end ?? null;

          if (dateFrom && dateTo) {
            if (dateFrom > dateTo) {
              return;
            }

            if (this.planVersionId === null) {
              if (this.isFetchingPlanVersion) {
                return; // a fetch is already in flight — don't fire a second one
              }
              this.isFetchingPlanVersion = true;
              this.planEntryService.getCurrentPlanVersion().subscribe({
                next: (response) => {
                  this.planVersionId = response.plan_version_id;
                  this.isFetchingPlanVersion = false;
                  this.requestLoadPlanningEntries(dateFrom, dateTo);
                },
                error: (error) => {
                  this.isFetchingPlanVersion = false;
                  console.error('Failed to get current plan version:', error);
                  this.errorMessage = 'Unable to load the plan. Please try again.';
                }
              });
            } else {
              this.requestLoadPlanningEntries(dateFrom, dateTo);
            }
          }
        }
      );

    // Loads planning entries for a date range once a plan version is known -
    // fetches normal and clash-flagged entries in parallel and merges them
    this.loadEntriesParams$
      .pipe(
        switchMap(({ dateFrom, dateTo, onSuccess }) => {
          this._snackBarService.showSnackBar(
            Constants.SNACKBAR_MSG_LOADING_PLAN_ENTRIES,
            null,
            'info',
            true
          );

          const baseParams = {
            dateFrom,
            dateTo,
            plan_version_id: String(this.planVersionId)
          };

          return forkJoin({
            normal: this.planEntryService.getPlanEntries({...baseParams,clash: '0'}),
            clashed: this.planEntryService.getPlanEntries({...baseParams,clash: '1'}),
            total: this.planEntryService.getPlanEntryTotal({...baseParams,clash: '0'})
          }).pipe(
            switchMap(result => [{ ...result, onSuccess }])
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: ({ normal, clashed, total, onSuccess  }) => {
          this.totalHours = Number(total?.TotalHours ?? 0);

          const mappedNormal: PlanEntry[] = (normal || []).map((raw: any) => ({
            id: Number(raw.ID),
            date: raw.TDate ? raw.TDate.split('T')[0] : '',
            client: raw.Client,
            consultant: raw.Employee,
            entryOwner: raw.Entry_Owner ?? '',
            entryOwnerLoginName: raw.Entry_Owner_LoginName ?? '',
            company: String(raw.Company_id),
            companyId: Number(raw.Company_id),
            project: raw.Project,
            taskRef: raw.Task_name,
            task: raw.Task,
            hours: Number(raw.Hours),
            rate: Number(raw.Rate),
            allocation: '',
            charge: raw.Charge,
            invoiceDate: raw.Invoice_date ? raw.Invoice_date.split('T')[0] : '',
            seriesId: null,
            isUnsaved: false,
            taskId: raw.Task_id ?? null,
            currencyCode: raw.Currency_code ?? null,
            isClashEntry: false
          }));

          const mappedClashed: PlanEntry[] = (clashed || []).map((raw: any) => ({
            id: Number(raw.ID),
            date: raw.TDate ? raw.TDate.split('T')[0] : '',
            client: raw.Client,
            consultant: raw.Employee,
            entryOwner: raw.Entry_Owner ?? '',
            entryOwnerLoginName: raw.Entry_Owner_LoginName ?? '',
            company: String(raw.Company_id),
            companyId: Number(raw.Company_id),
            project: raw.Project,
            taskRef: raw.Task_Name,
            task: raw.Task,
            hours: Number(raw.Hours),
            rate: Number(raw.Rate),
            allocation: '',
            charge: raw.Charge,
            invoiceDate: raw.Invoice_date ? raw.Invoice_date.split('T')[0] : '',
            seriesId: null,
            isUnsaved: false,
            taskId: raw.Task_id ?? null,
            currencyCode: raw.Currency_code ?? null,
            isClashEntry: true
          }));

          const mapped: PlanEntry[] = [...mappedNormal, ...mappedClashed];

          const allDates = new Set(mapped.map(e => e.date));

          this.planningEntries = this.planningService.recomputeClashFlagsForDates(
            mapped,
            allDates
          );

          this.savedPlanningEntries = this.planningEntries.map(entry => ({
            ...entry
          }));

          this.serverEntryIds = new Set(mapped.map(entry => entry.id));

          this._snackBarService.dismissSnackBar();

          onSuccess?.();
        },
        error: (err) => {
          console.error('Failed to load planning entries', err);

          const backendMessage =
            err?.error?.Message ??
            err?.error?.ErrorMessage ??
            null;

          this._snackBarService.dismissSnackBar();
          this._snackBarService.showSnackBar(
            backendMessage ?? Constants.SNACKBAR_MSG_PLAN_ENTRIES_LOAD_FAILED,
            'close',
            'failure'
          );
        }
      });

    this.planningService.showClashes$
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.showClashCount = value;
      });

    this._store.select(getLoggedInUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (loggedInUser: any) => {
          this.loggedInUsername = loggedInUser?.username ?? '';
        },
        error: (error: any) => {
          console.error('Failed to get logged-in user:', error);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Pushes a load request onto loadEntriesParams$, which the switchMap
  // subscription above picks up and turns into the actual API calls
  private requestLoadPlanningEntries(
    dateFrom: string,
    dateTo: string,
    onSuccess?: () => void
  ): void {
    if (this.planVersionId === null) {
      console.error('Plan version ID is not available.');
      return;
    }

    this.loadEntriesParams$.next({
      dateFrom,
      dateTo,
      client: this.activeFilters.clients.join(','),
      project: this.activeFilters.projects.join(','),
      consultant: this.activeFilters.employees.join(','),
      charge: this.activeFilters.chargedFilter,
      onSuccess
    });
  }

  // ----- Create plan -----

  openAddPlanDialog(): void {
    this.entryPanelData = null;
    this.showEntryPanel = true;
  }

  // ----- Edit plan -----

  onEditEntryRequested(entry: PlanEntry): void {
    this.entryPanelData = entry;
    this.showEntryPanel = true;
  }

  // ----- Plan panel result -----

  onEntryPanelResult(result: PlanDialogResult): void {
    this.applyDialogResult(result);

    // A new or edited entry now exists as an unsaved change.
    this.isSaveDisabled = false;
    

    this.showEntryPanel = false;
  }

  onEntryPanelCancelled(): void {
    this.showEntryPanel = false;
  }

  // ----- Apply plan changes -----

  private applyDialogResult(result: PlanDialogResult): void {
    const { entries, warnings } = this.planningService.applyDialogResult(
      this.planningEntries,
      result
    );
    this.planningEntries = entries;

    warnings.forEach(w => console.warn(w));
  }

  // ----- Table changes -----

  onEntriesChanged(entries: PlanEntry[]): void {
    this.planningEntries = entries;
    this.isSaveDisabled = false;
  }

  onFilteredEntriesChanged(entries: PlanEntry[]): void {
    this.visibleEntries = entries;

    this.totalHours = entries
      .filter(entry => !entry.isPlaceholder)
      .reduce((total, entry) => total + Number(entry.hours || 0), 0);
  }

  // ----- Selection -----

  updateSelection(selectedEntryIds: number[]): void {
    this.selectedEntryIds = selectedEntryIds;
  }

  // ----- Delete -----

  // Deletes selected entries: unsaved/local-only entries are removed
  // immediately, while server-backed entries are deleted via the API first
  // (clash rows before normal rows, since a clash row may depend on its
  // normal counterpart still existing server-side).
  deleteSelectedPlans(): void {
    if (!window.confirm(Constants.MSG_CONFIRM_DELETE_RECORDS)) {
      return;
    }

    const idsToDeleteOnServer = this.selectedEntryIds.filter(id =>
      this.serverEntryIds.has(id)
    );

    const applyLocalDelete = () => {
      const { entries, warnings } = this.planningService.deleteEntries(
        this.planningEntries,
        this.selectedEntryIds
      );

      this.planningEntries = entries;
      this.selectedEntryIds = [];
      this.isSaveDisabled = false;

      warnings.forEach(w => console.warn(w));
    };

    if (idsToDeleteOnServer.length === 0) {
      applyLocalDelete();
      return;
    }

    this._snackBarService.showSnackBar(
      Constants.SNACKBAR_MSG_SAVING_CHANGES,
      null,
      'info',
      true
    );

    const clashEntryIds = new Set(
      this.planningEntries
        .filter(entry => entry.isClashEntry)
        .map(entry => entry.id)
    );

    const clashIds = idsToDeleteOnServer.filter(id => clashEntryIds.has(id));
    const normalIds = idsToDeleteOnServer.filter(id => !clashEntryIds.has(id));

    // Runs once all server deletes have succeeded - syncs local state and
    // re-fetches the current range so the grid reflects the server's view
    const finish = () => {
      const deletedIds = new Set(idsToDeleteOnServer);
      this.savedPlanningEntries = this.savedPlanningEntries.filter(
        entry => !deletedIds.has(entry.id)
      );
      idsToDeleteOnServer.forEach(id => this.serverEntryIds.delete(id));

      this.selectedEntryIds = [];
      this.isSaveDisabled = true; // nothing pending after a clean server delete

      this._snackBarService.dismissSnackBar();

      if (this.activeFilters.dateStart && this.activeFilters.dateEnd) {
        this.requestLoadPlanningEntries(
          this.activeFilters.dateStart,
          this.activeFilters.dateEnd,
          () => {
            this._snackBarService.showSnackBar(
              Constants.SNACKBAR_MSG_PLAN_ENTRY_DELETED,
              '',
              'success'
            );
          }
        );
      } else {
        this._snackBarService.showSnackBar(
          Constants.SNACKBAR_MSG_PLAN_ENTRY_DELETED,
          '',
          'success'
        );
      }
    };

    const deleteNormals = () => {
      if (normalIds.length === 0) {
        finish();
        return;
      }
      forkJoin(
        normalIds.map(id => this.planEntryService.deletePlanEntry(id, false))
      ).subscribe({
        next: () => finish(),
        error: (err) => {
          console.error('Failed to delete one or more plan entries', err);

          const backendMessage =
            err?.error?.Message ??
            err?.error?.ErrorMessage ??
            null;

          this._snackBarService.dismissSnackBar();
          this._snackBarService.showSnackBar(
            backendMessage ?? Constants.SNACKBAR_MSG_PLAN_ENTRY_DELETE_FAILED,
            'close',
            'failure'
          );
        }
      });
    };

    if (clashIds.length === 0) {
      deleteNormals();
      return;
    }

    forkJoin(
      clashIds.map(id => this.planEntryService.deletePlanEntry(id, true))
    ).subscribe({
      next: () => deleteNormals(),
      error: (err) => {
        console.error('Failed to delete one or more plan entries', err);
        // Nothing removed locally if the clash-row deletes fail.

        const backendMessage =
          err?.error?.Message ??
          err?.error?.ErrorMessage ??
          null;

        this._snackBarService.dismissSnackBar();
        this._snackBarService.showSnackBar(
          backendMessage ?? Constants.SNACKBAR_MSG_PLAN_ENTRY_DELETE_FAILED,
          'close',
          'failure'
        );
      }
    });
  }

  // ----- Save -----

  // Saves all unsaved entries: existing ones via updatePlanEntry (PUT each),
  // new ones together in one savePlanEntriesBatch call. Batch ResultCode
  // 0 or -1 (saved as clash) = success. The batch isn't transactional, so
  // the grid is reloaded afterwards. Entries missing task/company/currency
  // are skipped.
  savePlanningEntries(): void {
    if (this.isSaveDisabled || this.isSaving) {
      return;
    }

    if (this.planVersionId === null) {
      console.error('Cannot save: plan version ID is not available.');
      return;
    }

    if (!this.loggedInUsername) {
      console.error('Cannot save: logged-in username is empty.');
      return;
    }

    const entriesToSave = this.planningEntries.filter(e => e.isUnsaved);
    if (entriesToSave.length === 0) {
      return;
    }

    const updateRequests: Observable<any>[] = [];
    const createPayloads: BatchPlanEntryPayload[] = [];
    let skippedCount = 0;

    for (const entry of entriesToSave) {
      if (entry.taskId == null) {
        console.error('Task ID is missing for plan entry', entry);
        skippedCount++;
        continue;
      }
      if (entry.companyId == null) {
        console.error('Company ID is missing for plan entry', entry);
        skippedCount++;
        continue;
      }
      if (!entry.currencyCode) {
        console.error('Currency code is missing for plan entry', entry);
        skippedCount++;
        continue;
      }

      const partialRejection = entry.allocation === 'Partial' ? 1 : 0;

      if (this.serverEntryIds.has(entry.id)) {
        // Existing entry: keep the PUT until the backend confirms batch handles updates
        updateRequests.push(
          this.planEntryService.updatePlanEntry(entry.id, {
            entryDate: entry.date,
            hours: entry.hours,
            charge: entry.charge,
            client: entry.client,
            project: entry.project,
            employee: entry.consultant,
            task: entry.task,
            rate: entry.rate,
            loginname: this.loggedInUsername,
            entryOwner: entry.entryOwner,
            invoice_date: entry.invoiceDate,
            currency_code: entry.currencyCode,
            task_id: entry.taskId,
            plan_version_id: this.planVersionId,
            IsClash: entry.isClashEntry ?? false,
            company_id: entry.companyId,
            checkforclashes: true,
            PartialRejection: partialRejection
          })
        );
      } else {
        createPayloads.push({
          entryDate: entry.date,
          hours: entry.hours,
          charge: entry.charge,
          client: entry.client,
          project: entry.project,
          employee: entry.consultant,
          task: entry.task,
          rate: entry.rate,
          loginname: this.loggedInUsername,
          entryOwner: entry.entryOwner,
          time_id: 0,
          invoice_date: entry.invoiceDate,
          currency_code: entry.currencyCode,
          task_id: entry.taskId,
          plan_version_id: this.planVersionId,
          IsClash: false,
          company_id: entry.companyId,
          checkforclashes: true,
          PartialRejection: partialRejection
        });
      }
    }

    const saveRequests: Observable<any>[] = [...updateRequests];
    if (createPayloads.length > 0) {
      saveRequests.push(this.planningService.savePlanEntriesBatch(createPayloads));
    }

    // Nothing to send: every unsaved entry was skipped for a missing field
    if (saveRequests.length === 0) {
      this._snackBarService.showSnackBar(
        Constants.SNACKBAR_MSG_PLAN_ENTRY_MISSING_FIELDS,
        'close',
        'failure'
      );
      return;
    }

    this.isSaving = true;
    this._snackBarService.showSnackBar(
      Constants.SNACKBAR_MSG_SAVING_CHANGES,
      null,
      'info',
      true
    );

    // Re-fetches the current range so the grid matches the server, then runs onDone
    const reloadThen = (onDone: () => void) => {
      if (this.activeFilters.dateStart && this.activeFilters.dateEnd) {
        this.requestLoadPlanningEntries(
          this.activeFilters.dateStart,
          this.activeFilters.dateEnd,
          onDone
        );
      } else {
        onDone();
      }
    };

    forkJoin(saveRequests).subscribe({
      next: (responses) => {
        // Batch response (if any) is the last item. ResultCode 0 or -1 = saved.
        let failedMessages: string[] = [];
        if (createPayloads.length > 0) {
          const batchResponse = responses[responses.length - 1] as BatchSaveResponse;
          const results = batchResponse?.Results ?? [];
          failedMessages = results
            .filter(r => r.ResultCode !== 0 && r.ResultCode !== -1)
            .map(r => r.Message ?? '');
        }

        reloadThen(() => {
          this.selectedEntryIds = [];
          this.isSaving = false;
          this._snackBarService.dismissSnackBar();

          if (failedMessages.length > 0) {
            this.isSaveDisabled = false;
            this._snackBarService.showSnackBar(
              failedMessages[0] || Constants.SNACKBAR_MSG_PLAN_ENTRY_PARTIAL_SAVE,
              'close',
              'failure'
            );
          } else if (skippedCount > 0) {
            // The reload replaced the grid with server data, so skipped rows are gone
            this.isSaveDisabled = true;
            this._snackBarService.showSnackBar(
              Constants.SNACKBAR_MSG_PLAN_ENTRY_MISSING_FIELDS,
              'close',
              'failure'
            );
          } else {
            this.isSaveDisabled = true;
            this._snackBarService.showSnackBar(
              Constants.SNACKBAR_MSG_PLAN_ENTRY_SAVED,
              '',
              'success'
            );
          }
        });
      },

      error: (error) => {
        console.error('Plan entry save failed:', error?.error ?? error);

        const backendMessage = error?.error?.Message ?? error?.error?.ErrorMessage ?? null;
        const message = error?.status === 503
          ? Constants.SNACKBAR_MSG_SERVER_BUSY_RETRY
          : backendMessage ?? Constants.SNACKBAR_MSG_PLAN_ENTRY_SAVE_FAILED;

        // The batch is not transactional, so earlier entries may already be saved.
        const showError = () => {
          this.isSaving = false;
          this.isSaveDisabled = false;
          this._snackBarService.dismissSnackBar();
          this._snackBarService.showSnackBar(message, 'close', 'failure');
        };

        const partiallySaved = (error?.error?.SavedEntries?.length ?? 0) > 0;
        if (partiallySaved) {
          reloadThen(showError);
        } else {
          showError();
        }
      }
    });
  }

  // ----- Reset -----

  resetPlanningEntries(): void {
    if (!window.confirm(Constants.MSG_CONFIRM_RESET_ENTRY)) {
      return;
    }

    this.planningEntries = this.savedPlanningEntries.map(entry => ({
      ...entry
    }));

    this.selectedEntryIds = [];
    this.showEntryPanel = false;
    this.isSaveDisabled = true;
  }

}